(() => {
  if (customElements.get('tf-video-watch')) return;
  const cache = new Map();
  let muted = true;
  let navigating = false;
  let wheelAt = 0;
  let preloadVideo;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const saveData = navigator.connection?.saveData;
  const localUrl = (url) => new URL(url, location.origin);
  const validUrl = (url) => url.origin === location.origin && url.pathname.includes('/pages/watch/');
  const getDocument = async (href) => {
    const url = localUrl(href);
    if (!validUrl(url)) throw new Error('Invalid watch URL');
    if (!cache.has(url.pathname)) {
      cache.set(url.pathname, fetch(url.pathname, { credentials: 'same-origin' }).then(async response => {
        if (!response.ok) throw new Error('Video page unavailable');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (!doc.querySelector('tf-video-watch video source[src]')) throw new Error('Video page unavailable');
        return doc;
      }).catch(error => { cache.delete(url.pathname); throw error; }));
      if (cache.size > 6) cache.delete(cache.keys().next().value);
    }
    return cache.get(url.pathname);
  };
  const navigate = async (href, push = true) => {
    const url = localUrl(href);
    if (navigating || !validUrl(url)) return;
    const current = document.querySelector('tf-video-watch');
    if (!current) return;
    navigating = true;
    current.video.pause();
    current.setAttribute('aria-busy', 'true');
    current.status.textContent = current.dataset.loading;
    try {
      const doc = await getDocument(url.href);
      if (push) history.pushState({ tfVideo: true }, '', url.pathname + url.search);
      document.title = doc.title;
      for (const selector of ['link[rel="canonical"]', 'meta[name="description"]', 'meta[property="og:title"]', 'meta[property="og:description"]', 'meta[property="og:url"]', 'meta[property="og:image"]']) {
        const fresh = doc.querySelector(selector);
        const old = document.querySelector(selector);
        if (fresh && old) old.replaceWith(fresh.cloneNode(true));
      }
      current.replaceWith(doc.querySelector('tf-video-watch').cloneNode(true));
      window.scrollTo({ top: 0, behavior: 'instant' });
      document.querySelector('tf-video-watch .tf-stage')?.focus({ preventScroll: true });
    } catch {
      location.assign(url.href);
    } finally { navigating = false; }
  };
  class FishWatch extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.video = this.querySelector('video');
      this.stage = this.querySelector('.tf-stage');
      this.status = this.querySelector('.tf-status');
      if (!this.video) return;
      this.video.controls = false;
      this.video.muted = muted;
      this.querySelector('.tf-player-controls').hidden = false;
      const mobile = matchMedia('(max-width: 760px), (max-width: 1024px) and (max-height: 520px)');
      this.querySelector('.tf-video-about')?.toggleAttribute('open', !mobile.matches);
      mobile.addEventListener('change', () => this.querySelector('.tf-video-about')?.toggleAttribute('open', !mobile.matches), { signal: this.abort.signal });
      this.playlist = new URLSearchParams(location.search).get('playlist') || this.dataset.playlist;
      const lists = Array.from(this.querySelectorAll('[data-playlist-handle]'));
      let list = lists.find(item => item.dataset.playlistHandle === this.playlist && Array.from(item.querySelectorAll('a')).some(a => localUrl(a.href).pathname === this.dataset.url));
      if (!list) { this.playlist = this.dataset.playlist; list = lists.find(item => item.dataset.playlistHandle === this.playlist); }
      this.items = list ? Array.from(list.querySelectorAll('a')).map(a => ({ href: localUrl(a.href).pathname, title: a.textContent })) : [];
      const index = this.items.findIndex(item => item.href === this.dataset.url);
      this.previous = this.items[index - 1]?.href;
      this.next = this.items[index + 1]?.href;
      const contextual = href => href + '?playlist=' + encodeURIComponent(this.playlist);
      for (const [selector, href] of [['[data-prev]', this.previous], ['[data-next]', this.next]]) {
        const a = this.querySelector(selector);
        if (href) a.href = contextual(href);
        else { a.removeAttribute('href'); a.setAttribute('aria-disabled', 'true'); a.setAttribute('tabindex', '-1'); }
      }
      if (list) {
        const label = this.querySelector('.tf-playlist-link');
        label.textContent = list.dataset.title;
        label.href = list.dataset.url;
        const ol = this.querySelector('.tf-playlist ol');
        ol.replaceChildren(...this.items.map(item => {
          const li = document.createElement('li');
          const a = document.createElement('a');
          a.textContent = item.title; a.href = contextual(item.href); a.dataset.watchLink = '';
          if (item.href === this.dataset.url) a.setAttribute('aria-current', 'page');
          li.append(a); return li;
        }));
        this.querySelector('.tf-playlist summary span').textContent = this.items.length;
      }
      const on = (target, type, fn, opts = {}) => target.addEventListener(type, fn, { ...opts, signal: this.abort.signal });
      on(this, 'click', async event => {
        const link = event.target.closest('[data-watch-link],[data-prev],[data-next]');
        if (link && link.hasAttribute('href') && !event.ctrlKey && !event.metaKey && !event.shiftKey && event.button === 0) { event.preventDefault(); navigate(link.href); return; }
        const action = event.target.closest('[data-action]')?.dataset.action;
        if (action === 'details') {
          event.preventDefault();
          this.video.pause();
          this.querySelector('.tf-video-about')?.setAttribute('open', '');
          const details = this.querySelector('.tf-watch-info');
          details.scrollIntoView({ block: 'start', behavior: 'instant' });
          details.focus({ preventScroll: true });
        }
        if (action === 'return-video') {
          event.preventDefault();
          this.stage.scrollIntoView({ block: 'start', behavior: 'instant' });
          this.stage.focus({ preventScroll: true });
          this.play();
        }
        if (action === 'play') this.video.paused ? this.play() : this.video.pause();
        if (action === 'mute') { muted = !this.video.muted; this.video.muted = muted; this.updateButtons(); }
        if (action === 'retry') { this.querySelector('.tf-video-error').hidden = true; this.video.load(); this.play(); }
        if (action === 'fullscreen') {
          try {
            if (document.fullscreenElement) await document.exitFullscreen();
            else if (this.stage.requestFullscreen) await this.stage.requestFullscreen();
            else if (this.video.webkitEnterFullscreen) this.video.webkitEnterFullscreen();
            else this.stage.classList.toggle('tf-theater');
          } catch { this.stage.classList.toggle('tf-theater'); }
        }
        if (action === 'share') {
          const url = new URL(this.dataset.url, location.origin).href;
          try { if (navigator.share) await navigator.share({ title: document.title, url }); else { await navigator.clipboard.writeText(url); this.status.textContent = 'Link copied'; setTimeout(() => { this.status.textContent = ''; }, 2500); } } catch { this.status.textContent = url; }
        }
      });
      const advance = direction => {
        const href = direction > 0 ? this.next : this.previous;
        if (href) navigate(contextual(href));
      };
      let gesture;
      on(this.stage, 'touchstart', event => { if (event.touches.length === 1 && !event.target.closest('a,button,input,select,textarea,summary')) gesture = { x: event.touches[0].clientX, y: event.touches[0].clientY, time: performance.now() }; else gesture = null; }, { passive: true });
      on(this.stage, 'touchend', event => {
        if (!gesture || !event.changedTouches.length) return;
        const dx = event.changedTouches[0].clientX - gesture.x;
        const dy = event.changedTouches[0].clientY - gesture.y;
        if (Math.abs(dy) > 55 && Math.abs(dy) > Math.abs(dx) * 1.4 && performance.now() - gesture.time < 1000) advance(dy < 0 ? 1 : -1);
        gesture = null;
      }, { passive: true });
      on(this.stage, 'touchcancel', () => { gesture = null; }, { passive: true });
      on(this.stage, 'wheel', event => {
        if (event.ctrlKey || Math.abs(event.deltaY) < 18 || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
        if ((event.deltaY > 0 && this.next) || (event.deltaY < 0 && this.previous)) event.preventDefault();
        if (performance.now() - wheelAt > 900) { wheelAt = performance.now(); advance(event.deltaY > 0 ? 1 : -1); }
      }, { passive: false });
      on(this.stage, 'keydown', event => {
        if (event.target.closest('a,button,input,select,textarea,summary')) return;
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); advance(event.key === 'ArrowDown' ? 1 : -1); }
        if (event.key === ' ') { event.preventDefault(); this.video.paused ? this.play() : this.video.pause(); }
        if (event.key === 'Escape') this.stage.classList.remove('tf-theater');
      });
      on(this.video, 'play', () => { this.updateButtons(); this.prefetch(); });
      on(this.video, 'pause', () => { this.updateButtons(); this.setBuffering(false); });
      on(this.video, 'waiting', () => this.setBuffering(true));
      on(this.video, 'playing', () => this.setBuffering(false));
      on(this.video, 'canplay', () => this.setBuffering(false));
      on(this.video, 'timeupdate', () => { this.querySelector('.tf-progress span').style.transform = 'scaleX(' + (this.video.currentTime / (this.video.duration || 1)) + ')'; });
      on(this.video, 'error', () => { this.setBuffering(false); this.querySelector('.tf-video-error').hidden = false; });
      on(document, 'visibilitychange', () => { if (document.hidden) this.video.pause(); });
      this.observer = new IntersectionObserver(entries => {
        for (const entry of entries) if (!entry.isIntersecting) this.video.pause();
      }, { threshold: .35 });
      this.observer.observe(this.stage);
      this.updateButtons();
      if (!reduced && !saveData && !document.hidden) this.play();
    }
    async play() { try { if (this.video.readyState < 3) this.setBuffering(true); await this.video.play(); } catch { this.setBuffering(false); this.updateButtons(); } }
    setBuffering(pending) {
      clearTimeout(this.bufferTimer);
      const indicator = this.querySelector('.tf-buffering');
      if (!indicator) return;
      if (!pending) indicator.hidden = true;
      else this.bufferTimer = setTimeout(() => { if (this.isConnected && !this.video.paused && this.video.readyState < 3) indicator.hidden = false; }, 350);
    }
    updateButtons() {
      const play = this.querySelector('[data-action=play]');
      play.dataset.paused = String(this.video.paused);
      play.setAttribute('aria-label', this.video.paused ? this.dataset.play : this.dataset.pause);
      const sound = this.querySelector('[data-action=mute]');
      sound.dataset.muted = String(this.video.muted);
      sound.setAttribute('aria-label', this.video.muted ? this.dataset.muted : this.dataset.sound);
      play.title = play.getAttribute('aria-label');
      sound.title = sound.getAttribute('aria-label');
    }
    async prefetch() {
      if (!this.next || saveData || this.prefetched) return;
      this.prefetched = true;
      try {
        const doc = await getDocument(this.next);
        if (!this.isConnected) return;
        if (preloadVideo) { preloadVideo.removeAttribute('src'); preloadVideo.load(); }
        preloadVideo = document.createElement('video');
        preloadVideo.muted = true; preloadVideo.preload = 'auto';
        preloadVideo.src = doc.querySelector('video source').src;
        preloadVideo.load();
      } catch { this.prefetched = false; }
    }
    disconnectedCallback() { clearTimeout(this.bufferTimer); this.video?.pause(); this.abort?.abort(); this.observer?.disconnect(); }
  }
  customElements.define('tf-video-watch', FishWatch);
  addEventListener('popstate', () => { if (validUrl(localUrl(location.href))) navigate(location.href, false); else location.reload(); });
  addEventListener('pagehide', () => { document.querySelector('tf-video-watch video')?.pause(); if (preloadVideo) { preloadVideo.removeAttribute('src'); preloadVideo.load(); } });
})();
